import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { CompanyCodeListNotificationEmailsSetting, CompanyLocationWithOpeningHours } from '@api/ringil-api.v2';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import RichTextEditor from '@components/RingilComponents/RichTextEditor/RichTextEditor.tsx';
import RingilInputEmails from '@components/RingilComponents/InputEmails/RingilInputEmails.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgPlus from '@icons/plus.svg?react';
import SvgDelete from '@icons/delete-trash.svg?react';

export interface CostCenterNotificationRowError {
  companyLocationId?: string;
  cityMatcher?: string;
  emails?: string;
}

const firstMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.find(item => typeof item === 'string') as string | undefined;
  return undefined;
};

export const toRowErrors = (errors: unknown): CostCenterNotificationRowError[] =>
  (Array.isArray(errors) ? errors : []).map(rowError => ({
    companyLocationId: firstMessage(rowError?.companyLocationId),
    cityMatcher: firstMessage(rowError?.cityMatcher),
    emails: firstMessage(rowError?.emails),
  }));

export const emailSetsSchema = (invalidEmailMessage: string) =>
  yup.array().of(
    yup.object().shape({
      emails: yup.array().of(yup.string().email(invalidEmailMessage)),
    }),
  );

export const duplicateRowsError = <T,>(
  context: yup.TestContext,
  value: T[],
  key: (row: T) => string,
  field: 'companyLocationId' | 'cityMatcher',
  message: string,
  include: (row: T) => boolean = () => true,
): boolean | yup.ValidationError => {
  const seen = new Set<string>();
  const duplicates: number[] = [];
  (value ?? []).forEach((row, index) => {
    if (!include(row)) return;
    const k = key(row);
    if (seen.has(k)) duplicates.push(index);
    else seen.add(k);
  });
  if (duplicates.length === 0) return true;
  const errors = duplicates.map(index => context.createError({ path: `${context.path}[${index}].${field}`, message }));
  return errors.length === 1 ? errors[0] : new yup.ValidationError(errors);
};

interface ComponentProps {
  htmlContent?: string;
  notificationEmails: CompanyCodeListNotificationEmailsSetting[];
  onChangeHtmlContent: (value: string) => void;
  onChangeNotificationEmails: (value: CompanyCodeListNotificationEmailsSetting[]) => void;
  showHtmlContent?: boolean;
  showLocationSets?: boolean;
  showCitySets?: boolean;
  alwaysShowHtmlContent?: boolean;
  rowErrors?: CostCenterNotificationRowError[];
}

const DEFAULT_LOCATION = '__default__';

const CostCenterNotificationFields: React.FC<ComponentProps> = ({
  htmlContent,
  notificationEmails,
  onChangeHtmlContent,
  onChangeNotificationEmails,
  showHtmlContent = false,
  showLocationSets = false,
  showCitySets = false,
  alwaysShowHtmlContent = false,
  rowErrors,
}) => {
  const { tr } = useTranslationLgs();
  const { getLoVCompanyLocations } = useContext(ApiContext);

  const [htmlContentVisible, setHtmlContentVisible] = useState(alwaysShowHtmlContent || !!htmlContent);

  const [locations, setLocations] = useState<CompanyLocationWithOpeningHours[]>([]);
  useEffect(() => {
    if (!showLocationSets) return;
    getLoVCompanyLocations(false, d => setLocations(d ?? []));
  }, [showLocationSets]);

  const locationOptions = [
    { label: tr('EditCompanyCatalogItemSidebar.defaultLocation', 'Default (any other plant)'), value: DEFAULT_LOCATION },
    ...locations.map(l => ({ label: l.name, value: l.id })),
  ];

  // notificationEmails is a single flat list of two row kinds, split by `cityMatch`.
  // We keep the absolute index so each group maps back to the one array.
  const emailSets = notificationEmails ?? [];
  const locationSets = emailSets.map((s, i) => ({ s, i })).filter(x => !x.s.cityMatch);
  const citySets = emailSets.map((s, i) => ({ s, i })).filter(x => x.s.cityMatch);

  const addLocationSet = () => onChangeNotificationEmails([...emailSets, { cityMatch: false, emails: [] }]);
  const addCitySet = () => onChangeNotificationEmails([...emailSets, { cityMatch: true, cityMatcher: '', emails: [] }]);
  const removeSetAt = (index: number) => onChangeNotificationEmails(emailSets.filter((_, i) => i !== index));
  const updateAt = (index: number, patch: Partial<CompanyCodeListNotificationEmailsSetting>) =>
    onChangeNotificationEmails(emailSets.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const setLocationAt = (index: number, locationId: string) =>
    updateAt(index, { companyLocationId: locationId === DEFAULT_LOCATION ? undefined : locationId });

  return (
    <>
      {showHtmlContent &&
        (htmlContentVisible ? (
          <div className={'flex flex-col gap-2'}>
            <div className={'flex flex-row items-center justify-between'}>
              <span className={'text-sm font-medium text-content-secondary'}>{tr('EditCompanyCatalogItemSidebar.htmlContent', 'HTML content')}</span>
              {!alwaysShowHtmlContent && (
                <RingilButton
                  mode={'table'}
                  icon={<SvgDelete />}
                  onClick={() => {
                    onChangeHtmlContent('');
                    setHtmlContentVisible(false);
                  }}
                />
              )}
            </div>
            <RichTextEditor value={htmlContent} onChange={v => onChangeHtmlContent(v)} />
          </div>
        ) : (
          <RingilButton
            mode={'link'}
            icon={<SvgPlus />}
            label={tr('EditCompanyCatalogItemSidebar.addHtmlContent', 'Add HTML content')}
            onClick={() => setHtmlContentVisible(true)}
          />
        ))}

      {(showLocationSets || showCitySets) && (
        <div className={'flex flex-col gap-4'}>
          {/* Per-location sets — matched against the expedition's plants (incl. "any other plant" fallback). */}
          {showLocationSets && (
            <Field label={tr('EditCompanyCatalogItemSidebar.perLocationSets', 'E-mail sets per location')}>
              <div className={'flex flex-col gap-3'}>
                {locationSets.map(({ s, i }) => (
                  <div key={i} className={'flex flex-col gap-2 rounded-lg border border-content-tertiary p-3'}>
                    <div className={'flex flex-row items-center justify-end'}>
                      <RingilButton mode={'table'} icon={<SvgDelete />} onClick={() => removeSetAt(i)} />
                    </div>
                    <Field label={tr('EditCompanyCatalogItemSidebar.plant', 'Plant / destination')} errorMessage={rowErrors?.[i]?.companyLocationId}>
                      <Dropdown value={s.companyLocationId ?? DEFAULT_LOCATION} onChange={e => setLocationAt(i, e.value)} options={locationOptions} filter />
                    </Field>
                    <Field label={tr('EditCompanyCatalogItemSidebar.emails', 'E-mail addresses')} errorMessage={rowErrors?.[i]?.emails}>
                      <RingilInputEmails
                        value={s.emails ?? []}
                        onChange={v => updateAt(i, { emails: v ?? [] })}
                        placeholder={tr('EditCompanyCatalogItemSidebar.emailsPlaceholder', 'Type an e-mail and press Enter')}
                      />
                    </Field>
                  </div>
                ))}
                <RingilButton
                  mode={'link'}
                  icon={<SvgPlus />}
                  label={tr('EditCompanyCatalogItemSidebar.addEmailSetPerLocation', 'Add e-mail set per location')}
                  onClick={addLocationSet}
                />
              </div>
            </Field>
          )}

          {/* By-city sets — matched against the expedition city (empty matcher = all cities). */}
          {showCitySets && (
            <Field label={tr('EditCompanyCatalogItemSidebar.byCitySets', 'E-mail sets by city')}>
              <div className={'flex flex-col gap-3'}>
                {citySets.map(({ s, i }) => (
                  <div key={i} className={'flex flex-col gap-2 rounded-lg border border-content-tertiary p-3'}>
                    <div className={'flex flex-row items-center justify-end'}>
                      <RingilButton mode={'table'} icon={<SvgDelete />} onClick={() => removeSetAt(i)} />
                    </div>
                    <Field label={tr('EditCompanyCatalogItemSidebar.cityMatcher', 'City matcher')} errorMessage={rowErrors?.[i]?.cityMatcher}>
                      <RingilInputText
                        value={s.cityMatcher ?? ''}
                        onChange={v => updateAt(i, { cityMatcher: v })}
                        fullWidth
                        placeholder={tr('EditCompanyCatalogItemSidebar.cityMatcherAllHint', 'Empty = all cities')}
                      />
                    </Field>
                    <Field label={tr('EditCompanyCatalogItemSidebar.emails', 'E-mail addresses')} errorMessage={rowErrors?.[i]?.emails}>
                      <RingilInputEmails
                        value={s.emails ?? []}
                        onChange={v => updateAt(i, { emails: v ?? [] })}
                        placeholder={tr('EditCompanyCatalogItemSidebar.emailsPlaceholder', 'Type an e-mail and press Enter')}
                      />
                    </Field>
                  </div>
                ))}
                <RingilButton
                  mode={'link'}
                  icon={<SvgPlus />}
                  label={tr('EditCompanyCatalogItemSidebar.addEmailSetByCity', 'Add e-mail set by city')}
                  onClick={addCitySet}
                />
              </div>
            </Field>
          )}
        </div>
      )}
    </>
  );
};

export default CostCenterNotificationFields;
